import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { useAuthUser } from "@/App";
import { IMPERSONATION_ENDED_EVENT, endImpersonation, getImpersonationStatus } from "@/lib/impersonation";

type BannerState = "active" | "ended" | "expired";

/**
 * Admin "Login as user" indicator (v7.54). Renders only in a window that
 * carries an impersonation token: a slim pill while the session is live, and
 * a full-screen "closed" card once it ends (End session, server-side expiry,
 * or the admin logging out in the original window).
 */
export function ImpersonationBanner() {
  const user = useAuthUser();
  const [state, setState] = useState<BannerState | null>(() => getImpersonationStatus());
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    const onEnded = (e: Event) => {
      const reason = (e as CustomEvent<{ reason?: string }>).detail?.reason;
      setState(reason === "expired" ? "expired" : "ended");
    };
    window.addEventListener(IMPERSONATION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(IMPERSONATION_ENDED_EVENT, onEnded);
  }, []);

  if (!state) return null;

  if (state !== "active") {
    return (
      <div
        data-testid="impersonation-ended"
        className="fixed inset-0 z-[200] flex items-center justify-center p-6"
        style={{ backgroundColor: "#1A1618", color: "#f2eef2" }}
      >
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">
            {state === "expired" ? "Login-as session expired" : "Login-as session ended"}
          </h1>
          <p className="mt-2 text-sm" style={{ color: "#9a9098" }}>
            You can close this window. Your admin login in the original window is untouched.
          </p>
          <button
            onClick={() => window.close()}
            className="mt-4 rounded-lg px-3 py-1.5 text-sm font-medium"
            style={{ backgroundColor: "#7b1f40", color: "#ffffff" }}
          >
            Close window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[100] max-w-[calc(100%-1rem)]"
    >
      <div
        className="flex items-center gap-2 rounded-full py-1 pl-3 pr-1.5 text-xs shadow-lg"
        style={{ backgroundColor: "#1A1618", border: "1px solid #322a2c", color: "#f2eef2" }}
      >
        <Eye className="h-3.5 w-3.5 shrink-0" style={{ color: "#e0a97f" }} />
        <span className="truncate">
          Admin view · logged in as <span className="font-medium">{user?.email ?? "…"}</span>
        </span>
        <button
          onClick={async () => {
            setEnding(true);
            await endImpersonation();
          }}
          disabled={ending}
          data-testid="impersonation-end"
          className="shrink-0 rounded-full px-2.5 py-1 font-medium disabled:opacity-60"
          style={{ backgroundColor: "#7b1f40", color: "#ffffff" }}
        >
          {ending ? "Ending…" : "End session"}
        </button>
      </div>
    </div>
  );
}
